/**
 * The skills installed on this machine, desktop only: the person's own (~/.claude/skills) and the
 * open project's (.claude/skills), each with a box that is checked while it is on. Unchecking moves
 * the folder aside (skills-disabled/) so Claude Code stops using it; nothing is deleted.
 *
 * "Instalar pelo repositório" sends the catalog prompt `skills.install-from-repo` to the person's
 * own AI as a work order; when it finishes, the list shows what it installed.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { inventoryChanged } from '../lib/machineInventory'
import { Download, Loader2, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { skillSetEnabled, skillsList, type MachineSkill } from '../lib/desktop'
import { useActiveRepository } from '../lib/activeRepository'
import { folderFor } from '../lib/repoFolders'
import { localized, usePromptCatalog } from '../lib/prompts'
import { useOrderResult } from '../lib/useOrderResult'
import { checkRepoUrl, fillUrl, readInstalledSkills } from '../lib/skillsAndMcps'
import { Badge, EmptyState, ErrorBanner, Panel, Skeleton } from './primitives'

const SWITCH_ERRORS = ['conflict', 'not-found', 'not-a-skill', 'unsafe-folder', 'invalid-name', 'folder-not-picked']

export function MachineSkills() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { active } = useActiveRepository()
  const project = active ? folderFor(active.name) : null
  const [url, setUrl] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string | null>(null)
  const { data: catalog } = usePromptCatalog()
  const { order, finished } = useOrderResult(orderId)

  const key = ['machine-skills', project]
  const { data, isLoading, error, refetch } = useQuery({ queryKey: key, queryFn: () => skillsList(project) })

  const toggle = useMutation({
    mutationFn: (skill: MachineSkill) => skillSetEnabled(skill.name, skill.scope, skill.scope === 'project' ? project : null, !skill.enabled),
    onMutate: () => setProblem(null),
    onError: (e) => {
      const code = String(e)
      setProblem(SWITCH_ERRORS.includes(code) ? t(`skillsMachine.error.${code}`) : t('skillsMachine.error.unknown', { detail: code }))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['machine-skills'] })
      inventoryChanged()
    },
  })

  const install = useMutation({
    mutationFn: async () => {
      const prompt = catalog?.prompts.find((p) => p.key === 'skills.install-from-repo')
      if (!prompt) throw new Error('catalog')
      return api.workOrders.create({ kind: 'Once', instructions: fillUrl(prompt.body, url), title: `${localized(prompt.title, locale)}: ${url.trim()}`.slice(0, 200), repositoryId: null })
    },
    onSuccess: (created) => {
      setOrderId(created.id)
      setUrl('')
    },
    onError: (e) => setProblem(e instanceof Error && e.message === 'catalog' ? t('skillsMachine.noCatalog') : t('error.generic')),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    const check = checkRepoUrl(url)
    if (check !== 'ok') {
      setProblem(t(check === 'credentials' ? 'skills.error.url_has_credentials' : 'skills.error.invalid_url'))
      return
    }
    install.mutate()
  }

  const installed = finished ? readInstalledSkills(order?.lastReply ?? order?.lastReport) : []

  return (
    <Panel
      title={t('skillsMachine.title')}
      actions={
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => void refetch()} aria-label={t('skillsMachine.refresh')} title={t('skillsMachine.refresh')}>
          <RefreshCw size={13} />
        </button>
      }
    >
      <div className="stack">
        <p className="tiny muted" style={{ margin: 0 }}>{t('skillsMachine.hint')}</p>

        <strong className="small">{t('skillsMachine.installTitle')}</strong>
        <form className="row row--tight" onSubmit={submit}>
          <input className="input mono" style={{ flex: 1, minWidth: 0 }} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://github.com/anthropics/skills" aria-label={t('skillsMachine.installTitle')} />
          <button type="submit" className="btn btn--primary" disabled={install.isPending || !url.trim()}>
            <Download size={14} /> {t('skillsMachine.install')}
          </button>
        </form>
        {orderId && !finished && (
          <div className="notice small row row--tight"><Loader2 size={13} className="spin" /> {t('skillsMachine.installing')}</div>
        )}
        {orderId && finished && (
          <div className="notice small">
            {order?.status === 'Done' || installed.length > 0
              ? installed.length > 0 ? t('skillsMachine.installed', { names: installed.join(', ') }) : t('skillsMachine.installedNone')
              : t('skillsMachine.installFailed')}
          </div>
        )}
        {problem && <ErrorBanner message={problem} />}

        {error && <ErrorBanner message={t('skillsMachine.error.unknown', { detail: String(error) })} onRetry={() => void refetch()} retryLabel={t('common.retry')} />}
        {isLoading && <Skeleton height={60} />}
        {data && data.length === 0 && <EmptyState title={t('skillsMachine.emptyTitle')} body={t('skillsMachine.emptyBody')} />}
        {data && data.length > 0 && (
          <ul className="machine-list">
            {data.map((skill) => (
              <li key={`${skill.scope}:${skill.name}`} className="machine-row">
                <label className="machine-row__check">
                  <input type="checkbox" checked={skill.enabled} disabled={toggle.isPending} onChange={() => toggle.mutate(skill)} aria-label={t(skill.enabled ? 'skillsMachine.turnOff' : 'skillsMachine.turnOn', { name: skill.name })} />
                </label>
                <div className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                  <div className="row row--tight row--wrap">
                    <strong className="mono small">{skill.name}</strong>
                    <Badge tone={skill.scope === 'project' ? 'info' : 'neutral'}>{t(skill.scope === 'project' ? 'skillsMachine.scopeProject' : 'skillsMachine.scopeUser')}</Badge>
                    {!skill.enabled && <Badge tone="warn">{t('skillsMachine.off')}</Badge>}
                  </div>
                  {skill.description && <span className="small">{skill.description}</span>}
                  {skill.source && <span className="tiny muted mono truncate" title={skill.source}>{skill.source}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  )
}
